import { DateTime } from "luxon";

import { IPricingGroupFigures, IPricingMembersRequest, PricingAlignTarget, PricingGroupKey, PricingGroupOrder } from "../models/pricing/IPricing";
import { computePriceDelta, IPriceDelta, PriceDeltaTone } from "./listingPriceDelta";
import PriceFormat from "./priceFormat";

/** The gap between the cheapest and dearest part in the group - the thing to close. */
export function pricingGroupSpread(group: IPricingGroupFigures): number {
  if (group.minPrice == null || group.maxPrice == null) return 0;
  return Math.max(0, group.maxPrice - group.minPrice);
}

/** Groups of two or more, ranked by widest spread (default) or by how many parts they hold. */
export function sortPricingGroups<T extends IPricingGroupFigures>(groups: T[], order: PricingGroupOrder): T[] {
  return [...groups]
    .filter((group) => group.totalCount >= 2)
    .sort((a, b) =>
      order === "mostParts"
        ? b.totalCount - a.totalCount || pricingGroupSpread(b) - pricingGroupSpread(a)
        : pricingGroupSpread(b) - pricingGroupSpread(a) || b.totalCount - a.totalCount
    );
}

/** The gap as a currency figure, or "—" when the group is already aligned. */
export function formatSpread(spread: number): string {
  if (spread <= 0) return "—";
  return PriceFormat(spread);
}

/** "£40 – £90", a single figure when every part is at the same price, or "—" when none is priced. */
export function formatPriceRange(group: { minPrice: number | null; maxPrice: number | null }): string {
  if (group.minPrice == null || group.maxPrice == null) return "—";
  if (group.minPrice === group.maxPrice) return PriceFormat(group.minPrice);
  return `${PriceFormat(group.minPrice)} – ${PriceFormat(group.maxPrice)}`;
}

export function formatYearSpan(group: { yearFrom: number | null; yearTo: number | null }): string {
  if (group.yearFrom == null && group.yearTo == null) return "";
  if (group.yearFrom == null || group.yearTo == null || group.yearFrom === group.yearTo) {
    return String(group.yearFrom ?? group.yearTo);
  }
  return `${group.yearFrom} – ${group.yearTo}`;
}

export function membersRequestFor(key: PricingGroupKey): IPricingMembersRequest {
  if (key.type === "partNumber") {
    return { partNumber: key.partNumber };
  }

  return {
    vehicleMake: key.make,
    vehicleModel: key.model,
    vehicleGeneration: key.generation ?? "",
    categoryId: key.categoryId,
  };
}

export function isSamePricingGroup(current: PricingGroupKey | null, next: PricingGroupKey): boolean {
  if (!current || current.type !== next.type) return false;
  if (current.type === "partNumber" && next.type === "partNumber") {
    return current.partNumber === next.partNumber;
  }
  if (current.type === "vehicle" && next.type === "vehicle") {
    return (
      current.make === next.make &&
      current.model === next.model &&
      (current.generation ?? "") === (next.generation ?? "") &&
      current.categoryId === next.categoryId
    );
  }
  return false;
}

/** Stable id for a price-together group, used as a checkbox value. */
export function pricingGroupKeyId(key: PricingGroupKey): string {
  if (key.type === "partNumber") return `pn:${key.partNumber}`;
  return `vh:${key.make}|${key.model}|${key.generation ?? ""}|${key.categoryId}`;
}

/** Preview of Lowest / Average from the selected members. Custom is the typed amount. */
export function previewAlignPrice(
  prices: Array<number | null | undefined>,
  target: PricingAlignTarget,
  customPrice?: number | null
): number | null {
  if (
    target === PricingAlignTarget.Intelligent ||
    target === PricingAlignTarget.GradeMarkdown ||
    target === PricingAlignTarget.LastSold ||
    target === PricingAlignTarget.LastSoldGradeMarkdown ||
    target === PricingAlignTarget.AverageSold ||
    target === PricingAlignTarget.AverageSoldGradeMarkdown
  ) {
    return null;
  }

  if (target === PricingAlignTarget.Custom) {
    if (customPrice == null || customPrice <= 0) return null;
    return Math.round(customPrice * 100) / 100;
  }

  const usable = prices.filter((price): price is number => price != null && price > 0);
  if (usable.length === 0) return null;
  if (target === PricingAlignTarget.Lowest) return Math.round(Math.min(...usable) * 100) / 100;
  return Math.round((usable.reduce((sum, price) => sum + price, 0) / usable.length) * 100) / 100;
}

/** Current group average vs intelligence first, then average sold — same 10% window as listing optimisation. */
export function groupMarketDelta(group: IPricingGroupFigures): IPriceDelta {
  return computePriceDelta(group.averagePrice, group.intelligenceAverage, group.averageSoldPrice);
}

export const MARKET_TONE_CLASSES: Record<PriceDeltaTone, string> = {
  above: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200",
  below: "bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200",
  within: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
  noSold: "bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200",
  none: "bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200",
};

export function marketGapCaption(delta: IPriceDelta): string {
  const percent = delta.fraction == null ? null : Math.round(Math.abs(delta.fraction) * 100);
  switch (delta.tone) {
    case "above":
      return percent != null ? `${percent}% too high` : "Too high";
    case "below":
      return percent != null ? `${percent}% too low` : "Too low";
    case "within":
      return percent == null || percent === 0 ? "In range" : `In range (${delta.fraction != null && delta.fraction > 0 ? "+" : "−"}${percent}%)`;
    case "noSold":
      return "In range · no sold data";
    default:
      return "No market price yet";
  }
}

/** Whole days since an API datetime, parsed as UTC like formatDateTime. */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const start = DateTime.fromISO(iso, { zone: "utc" });
  if (!start.isValid) return null;
  return Math.max(0, Math.floor(DateTime.utc().diff(start, "days").days));
}
